import React from 'react';
import { useTranslation } from 'react-i18next';

const SERVER_COLORS = {
  MENA: '#f97316',
  FR: '#3b82f6',
  IT: '#22c55e',
  DE: '#a855f7',
};

const SkeletonRow = () => (
  <tr>
    {[...Array(10)]?.map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-4 rounded animate-pulse"
          style={{ backgroundColor: '#1f1f1f', width: i === 0 ? '120px' : i === 1 ? '100px' : '80px' }}
        />
      </td>
    ))}
  </tr>
);

const ServerBadge = ({ server }) => {
  const color = SERVER_COLORS?.[server] || '#6b7280';
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-bold tracking-wider"
      style={{
        backgroundColor: `${color}18`,
        color,
        border: `1px solid ${color}30`,
        fontFamily: 'Sora, sans-serif',
      }}
    >
      {server || '—'}
    </span>
  );
};

const WhatsAppIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const LOCALE_MAP = { fr: 'fr-FR', en: 'en-GB', it: 'it-IT', de: 'de-DE' };

const CreatorsTable = ({ creators, loading, sortField, sortDir, onSort, onCreatorClick, agencyFilter }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = LOCALE_MAP[i18n.language] || 'fr-FR';
  const showDepartColumn = agencyFilter !== 'in_agency';

  const columns = [
    { key: 'username', label: t('admin.creators.colUsername', 'Username'), sortable: true },
    { key: 'display_name', label: t('admin.creators.colDisplayName', 'Display Name'), sortable: false },
    { key: 'tiktok_id', label: t('admin.creators.colTiktokId', 'TikTok ID'), sortable: false },
    { key: 'server', label: t('admin.creators.colServer', 'Serveur'), sortable: true },
    { key: 'manager_name', label: t('admin.creators.colManager', 'Manager'), sortable: true },
    { key: 'tier_current', label: t('admin.creators.colTier', 'Tier'), sortable: true },
    { key: 'whatsapp', label: t('admin.creators.colWhatsapp', 'WhatsApp'), sortable: false },
    { key: 'is_active', label: t('admin.creators.colStatus', 'Statut'), sortable: false },
    ...(showDepartColumn ? [{ key: 'departure_date', label: t('admin.creators.colDeparture', 'Départ'), sortable: false }] : []),
    { key: 'last_live_date', label: t('admin.creators.colLastLive', 'Dernier live'), sortable: true },
    { key: 'contract_end', label: t('admin.creators.colContractEnd', 'Fin contrat'), sortable: true },
  ];

  const today = new Date();
  today?.setHours(0, 0, 0, 0);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="ml-1 opacity-30">
          <path d="M5 2L8 5H2L5 2Z" fill="currentColor" />
          <path d="M5 8L2 5H8L5 8Z" fill="currentColor" />
        </svg>
      );
    }
    return (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="ml-1" style={{ color: '#E5202A' }}>
        {sortDir === 'asc' ? (
          <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
        ) : (
          <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
        )}
      </svg>
    );
  };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
              {columns?.map((col) => (
                <th
                  key={col?.key}
                  className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider select-none ${col?.sortable ? 'cursor-pointer hover:text-white' : ''}`}
                  style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif', backgroundColor: '#111111' }}
                  onClick={() => col?.sortable && onSort(col?.key)}
                >
                  <span className="flex items-center">
                    {col?.label}
                    {col?.sortable && <SortIcon field={col?.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(10)]?.map((_, i) => <SkeletonRow key={i} />)
            ) : creators?.length === 0 ? (
              <tr>
                <td colSpan={columns?.length} className="px-4 py-12 text-center" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.creators.noCreatorFound', 'Aucun créateur trouvé')}
                </td>
              </tr>
            ) : (
              creators?.map((creator) => {
                const isLeftAgency = creator?.agency_status === 'left_agency';
                const contractEnd = creator?.contract_end ? new Date(creator.contract_end) : null;
                const isExpired = contractEnd && contractEnd < today;
                const isActive = creator?.is_active;
                const whatsapp = creator?.whatsapp || null;
                const tierCurrent = creator?.tier_current;
                const lastLiveDate = creator?.last_live_date || null;

                return (
                  <tr
                    key={creator?.tiktok_id}
                    className="transition-colors duration-150 border-t"
                    style={{
                      borderColor: '#1f1f1f',
                      opacity: isLeftAgency ? 0.5 : 1,
                      filter: isLeftAgency ? 'grayscale(1)' : 'none',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {/* Username */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => !isLeftAgency && onCreatorClick(creator?.tiktok_id)}
                        className="font-medium hover:underline transition-colors"
                        style={{ color: '#E5202A', fontFamily: 'Inter, sans-serif', cursor: isLeftAgency ? 'default' : 'pointer' }}
                      >
                        {creator?.username || '—'}
                      </button>
                    </td>
                    {/* Display Name */}
                    <td className="px-4 py-3" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
                      {creator?.display_name || '—'}
                    </td>
                    {/* TikTok ID */}
                    <td className="px-4 py-3" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
                      {creator?.tiktok_id || '—'}
                    </td>
                    {/* Server */}
                    <td className="px-4 py-3">
                      <ServerBadge server={creator?.server} />
                    </td>
                    {/* Manager name */}
                    <td className="px-4 py-3" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
                      {creator?.manager_name || '—'}
                    </td>
                    {/* Tier */}
                    <td className="px-4 py-3">
                      {tierCurrent && tierCurrent !== '—' ? (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}
                        >
                          T{tierCurrent}
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    {/* WhatsApp */}
                    <td className="px-4 py-3">
                      {whatsapp ? (
                        <a
                          href={`https://wa.me/${whatsapp?.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e?.stopPropagation()}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold transition-colors hover:opacity-80"
                          style={{ backgroundColor: 'rgba(37,211,102,0.12)', color: '#25D366', border: '1px solid rgba(37,211,102,0.25)', textDecoration: 'none', fontFamily: 'Inter, sans-serif' }}
                        >
                          <WhatsAppIcon />
                          {whatsapp}
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.7 }}>
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          —
                        </span>
                      )}
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      {isLeftAgency ? (
                        <span style={{
                          fontSize: '10px', fontWeight: '600',
                          padding: '2px 6px', borderRadius: '4px',
                          backgroundColor: 'rgba(128,128,128,0.15)',
                          color: '#888888',
                          fontFamily: 'Inter, sans-serif',
                        }}>
                          {t('admin.creators.statusLeft', 'Parti')}
                        </span>
                      ) : isActive ? (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)' }}
                        >
                          {t('admin.creators.statusActive', 'Actif ce mois')}
                        </span>
                      ) : (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}
                        >
                          {t('admin.creators.statusInactive', 'Inactif ce mois')}
                        </span>
                      )}
                    </td>
                    {/* Départ column — only when agencyFilter !== 'in_agency' */}
                    {showDepartColumn && (
                      <td className="px-4 py-3">
                        {creator?.departure_date ? (
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Inter, sans-serif' }}>
                            {new Date(creator.departure_date)?.toLocaleDateString(dateLocale)}
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                    )}
                    {/* Last live date */}
                    <td className="px-4 py-3">
                      {lastLiveDate ? (
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Inter, sans-serif' }}>
                          {new Date(lastLiveDate)?.toLocaleDateString(dateLocale)}
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>{t('admin.creators.neverLive', 'Jamais')}</span>
                      )}
                    </td>
                    {/* Contract end */}
                    <td className="px-4 py-3">
                      {contractEnd ? (
                        <span
                          className="text-xs font-medium"
                          style={{
                            color: isExpired ? '#ef4444' : 'rgba(255,255,255,0.55)',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {creator?.contract_end}
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CreatorsTable;
